/**
 * 🎨 预设三层树工具函数
 *
 * 三层结构：工具集（toolset）→ 分组（group）→ 预设（preset）
 * 后端 /api/presets 返回 { toolsets: [{ id, name, sortOrder, groups: [{ id, name, sortOrder, presets: [...] }] }] }
 *
 * 这些工具供工具条 / 生成节点下拉 / 配置面板复用，统一三层数据的读取方式。
 */

// 把一个工具集内所有分组的预设按「分组顺序 → 组内顺序」拍平成一维数组
export function flattenToolsetPresets(toolset) {
  if (!toolset || !Array.isArray(toolset.groups)) return [];
  const out = [];
  toolset.groups.forEach(g => (g.presets || []).forEach(p => out.push(p)));
  return out;
}

// 工具集的「置顶预设」：拍平后的第一个（即排序最靠前的预设）。作为工具条图标展示。
export function getTopPreset(toolset) {
  const flat = flattenToolsetPresets(toolset);
  return flat.length > 0 ? flat[0] : null;
}

// 仅保留「至少含一个预设」的工具集（公开接口已过滤，这里兜底）
export function nonEmptyToolsets(tree) {
  const toolsets = Array.isArray(tree?.toolsets) ? tree.toolsets : [];
  return toolsets.filter(t => flattenToolsetPresets(t).length > 0);
}

// 在整棵树里按 presetId 找预设，返回 { preset, toolset, group } 或 null
export function findPreset(tree, presetId) {
  const toolsets = Array.isArray(tree?.toolsets) ? tree.toolsets : [];
  for (const t of toolsets) {
    for (const g of (t.groups || [])) {
      for (const p of (g.presets || [])) {
        if (String(p.id) === String(presetId)) return { preset: p, toolset: t, group: g };
      }
    }
  }
  return null;
}

// 找包含某预设的工具集（用于节点下拉「仅当前工具集内」切换）
export function findToolsetByPresetId(tree, presetId) {
  const hit = findPreset(tree, presetId);
  return hit ? hit.toolset : null;
}

// ✨ 在技能工具箱树里按 presetId 找技能，返回 { preset, toolset, group } 或 null
// 与 findPreset 逻辑相同，但用于技能工具箱的三层结构（复用相同的查找算法）
export function findSkillboxPreset(tree, presetId) {
  const toolsets = Array.isArray(tree?.toolsets) ? tree.toolsets : [];
  for (const t of toolsets) {
    for (const g of (t.groups || [])) {
      for (const p of (g.presets || [])) {
        if (String(p.id) === String(presetId)) return { preset: p, toolset: t, group: g };
      }
    }
  }
  return null;
}
